import { useState, useEffect, useCallback } from 'react';
import { fetchAdminUsers } from '../services/adminApi';
import UserDetail from '../components/users/UserDetail';

const ROLE_META = {
    admin: { label: 'Admin',  color: 'purple' },
    user:  { label: 'User',   color: 'gray'   },
    demo:  { label: 'Demo',   color: 'yellow' },
};

const ROLE_FILTER_OPTIONS = [
    { value: '',      label: 'Všetky roly' },
    { value: 'user',  label: 'User' },
    { value: 'admin', label: 'Admin' },
    { value: 'demo',  label: 'Demo' },
];

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

const PAGE_SIZE = 20;

export default function Users() {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [noTenant, setNoTenant] = useState(false);
    const [page, setPage] = useState(1);
    const [selectedId, setSelectedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchAdminUsers({
                search: search || undefined,
                role: roleFilter || undefined,
                noTenant: noTenant || undefined,
                page,
                limit: PAGE_SIZE,
            });
            setUsers(res.data.data);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch {
            setError('Nepodarilo sa načítať používateľov.');
        } finally {
            setLoading(false);
        }
    }, [search, roleFilter, noTenant, page]);

    useEffect(() => {
        const timer = setTimeout(load, search ? 400 : 0);
        return () => clearTimeout(timer);
    }, [load, search]);

    const handleRoleChange = (e) => { setRoleFilter(e.target.value); setPage(1); };
    const handleNoTenantChange = (e) => { setNoTenant(e.target.checked); setPage(1); };

    const userCount = (n) => {
        if (n === 1) return '1 používateľ';
        if (n < 5) return `${n} používatelia`;
        return `${n} používateľov`;
    };

    return (
        <>
            <div className="adm-page">
                <div className="adm-page-header">
                    <h1 className="adm-page-title">Používatelia</h1>
                    <p className="adm-page-subtitle">Zoznam všetkých registrovaných používateľov.</p>
                </div>

                <div className="adm-filters">
                    <input
                        className="adm-filter-input"
                        placeholder="Hľadať podľa mena, e-mailu alebo username..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                    <select
                        className="adm-filter-select"
                        value={roleFilter}
                        onChange={handleRoleChange}
                    >
                        {ROLE_FILTER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <label className="adm-filter-checkbox">
                        <input
                            type="checkbox"
                            checked={noTenant}
                            onChange={handleNoTenantChange}
                        />
                        Bez subjektu
                    </label>
                </div>

                {error && <div className="adm-error" style={{ marginBottom: 20 }}>{error}</div>}

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                        <span className="adm-spinner" />
                    </div>
                ) : users.length === 0 ? (
                    <div className="adm-empty">Žiadni používatelia nevyhovujú filtrom.</div>
                ) : (
                    <div className="adm-table-wrap">
                        <table className="adm-table">
                            <thead>
                                <tr>
                                    <th>Meno</th>
                                    <th>E-mail</th>
                                    <th>Username</th>
                                    <th>Rola</th>
                                    <th>Subjekty</th>
                                    <th>Registrácia</th>
                                    <th>Stav</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => {
                                    const role = ROLE_META[u.role] ?? { label: u.role, color: 'gray' };
                                    return (
                                        <tr
                                            key={u._id}
                                            className={`adm-row--clickable${selectedId === u._id ? ' adm-row--active' : ''}`}
                                            onClick={() => setSelectedId(u._id)}
                                        >
                                            <td className="adm-td-primary">
                                                {u.name} {u.surname}
                                                {u.blocked && <span className="adm-badge adm-badge--red" style={{ marginLeft: 8 }}>Blokovaný</span>}
                                            </td>
                                            <td>{u.email}</td>
                                            <td className="adm-td-mono">{u.username}</td>
                                            <td>
                                                <span className={`adm-badge adm-badge--${role.color}`}>{role.label}</span>
                                            </td>
                                            <td>
                                                {u.tenantCount === 0
                                                    ? <span className="adm-badge adm-badge--red">0</span>
                                                    : u.tenantCount
                                                }
                                            </td>
                                            <td>{formatDate(u.registrationDate)}</td>
                                            <td>
                                                <span className={`adm-badge adm-badge--${u.verified ? 'green' : 'yellow'}`}>
                                                    {u.verified ? 'Verifikovaný' : 'Neverifikovaný'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="adm-table-footer">
                            <span>{userCount(total)}</span>
                            {pages > 1 && (
                                <div className="adm-pagination">
                                    <button
                                        className="adm-page-btn"
                                        onClick={() => setPage(p => p - 1)}
                                        disabled={page === 1}
                                    >
                                        ‹
                                    </button>
                                    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                                        <button
                                            key={p}
                                            className={`adm-page-btn${p === page ? ' adm-page-btn--active' : ''}`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        className="adm-page-btn"
                                        onClick={() => setPage(p => p + 1)}
                                        disabled={page === pages}
                                    >
                                        ›
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {selectedId && (
                <UserDetail
                    userId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onUpdate={load}
                />
            )}
        </>
    );
}
